import { Promise } from 'rsvp';
import { A } from '@ember/array';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { convertToUTCDate } from "../../utils/date-and-time";

/**
 * Shared constants associated with the assignment-commands
 * @type {Object}
 */
export const ASSIGNMENT_COMMAND_CONSTANTS = {
  /**
   * These are the API enums we'll use for assignment creation.
   */
  CREATE_ASSIGNMENT_PROPS: {
    AMOUNT: 'amount',
    TITLE: 'title',
    TYPE: 'type',
    DRAFT: 'draft',
    FREELANCER_ID: 'freelancer_id',
    SCHEDULE_ITEMS: 'schedule',
    SERVICES: 'services',
    CLASSIFICATIONS: 'classification_ids',
    CUSTOM_CLASSIFICATIONS: 'custom_classifications',
    COST_CODE: 'cost_code',
    DEADLINE: 'deadline',
    DESCRIPTION: 'description',
    DELIVERABLES: 'assignment_deliverables',
    EXAMPLES: 'examples',
    EXPEDITE: 'expedite',
    GLOBAL: 'project_remote',
    CANDIDATE_GLOBAL: 'candidate_remote',
    IS_COMPLETE: 'is_complete',
    PRIVACY_LEVEL: 'privacy_level',
    COMPENSATION_MIN: 'compensation_min',
    COMPENSATION_MAX: 'compensation_max',
    MESSAGE: 'message',
    MIN_RATING: 'min_rating',
    ESTIMATED_DAYS: 'estimated_days',
    COUNTRY: 'country_id',
    CANDIDATE_COUNTRY: 'candidate_country_id',
    PLACE: 'place_id',
    CANDIDATE_PLACE: 'candidate_place_id',
    PRODUCTION: 'production_id',
    COMPANY_TEAM: 'company_team_id',
    URGENT: 'urgent',
    RATE_TYPE: 'rate_type',
    PRIVATE_COMMENT: 'private_comment',
    REGION: 'region_id',
    REVIEW_COMMENT: 'review_comment',
    REVIEW_SCORE: 'review_score',
    SELECT: 'select',
    STORY_CODE: 'story_code',
    EXPENSE: 'expense',
    SELLER_FEES: 'seller_fees_covered',
    DIRECT: 'direct',
    DIRECT_INFO: 'direct_info',
    DIRECT_SERVICE_IDS: 'service_ids',
    CLONED_ASSIGNMENT_ID: 'cloned_assignment_id',
    CREATED_FROM: 'created_from',
    CLIENT_UUID: 'client_uuid',
  },

  /** The enums for the created_from property. */
  CREATED_FROM: {
    CREATE: 'create',
    DIRECT: 'direct',
    DIY: 'diy',
    PITCH: 'pitch',
    STANDARD: 'standard',
  },

  /**
   * Direct hire API constants.
   */
  DIRECT: {
    NON_DIRECT: 0,
    DIRECT_NOT_COMPLETE: 1,
    DIRECT_COMPLETE: 2
  },

  /**
   * @type {Object}     Parameters for the testMatches for a posting API call.
   */
  TEST_MATCHES_PARAMS: {
    DEADLINE: 'deadline',               // (Required) Date - How long is the posting open?
    SERVICES: 'services',               // (Required) array - Array of service ids
    TYPE: 'type',                       // (Required) int - ASSIGNMENT.TYPES
    PRIVACY_LEVEL: 'privacy_level',     // (Required) int - ASSIGNMENT.PRIVACY_LEVEL
    RATE_TYPE: 'rate_type',             // (Required) int - ASSIGNMENT.RATE_TYPE
    COMPENSATION_MIN: 'compensation_min', // (Required) int
    COMPENSATION_MAX: 'compensation_max', // (Required) int
    MIN_RATING: 'min_rating',           // (Required) int (1-5)
    ESTIMATED_DAYS: 'estimated_days',   // (Required) int
    GLOBAL: 'project_remote',           // (Optional) bool - Whether global. Accepts only one type of location.
    CANDIDATE_GLOBAL: 'candidate_remote', // (Optional) bool - Whether candidate location is global. Accepts only one type of candidate location.
    COUNTRY: 'country_id',              // (Optional) int - Country id. Accepts only one type of location.
    CANDIDATE_COUNTRY: 'candidate_country_id', // (Optional) int - Country id. Accepts only one type of candidate location.
    REGION: 'region_id',                // (Optional) int - Region id. Accepts only one type of location.
    PLACE: 'place_id',                  // (Optional) int - Id of a Google Place. Accepts only one type of location.
    CANDIDATE_PLACE: 'candidate_place_id', // (Optional) int - Id of a Google Place. Accepts only one type of candidate location.
  },

  /**
   * Constants for the 'test-matches' endpoint
   */
  TEST_MATCHES_STATUS : {
    BLOCK: 'block',
    GOOD: 'good',
    NONE: 'none',
    WARN: 'warn'
  }
};

/**
 * Service for assignment related API calls.
 */
export default Service.extend({
  api: service(),
  store: service(),
  errorTracker: service('error-tracker'),
  shData: service(),

  /**
   * @param {DS.Model|string} assignment
   * @param {DS.Model|string} user
   * @param {boolean} favorite
   */
  setFavorite(assignment, user, favorite) {
    const assignmentId = this.shData.getModelId(assignment);
    const userId = this.shData.getModelId(user);

    const options = {
      jsonData: {
        favorite
      },
    };

    return this.get('api').post(`postings/${assignmentId}/users/${userId}/assignmentUserMeta`, options).then((response) => {
      return this._sideloadAssignmentUserMeta(assignmentId, response);
    }).catch((e) => {
      this.get('errorTracker').error('setFavorite - failed to set favorite ' + assignmentId, this, e);
      throw e;
    });
  },

  /**
   * @param {DS.Model|string} assignment
   * @param {DS.Model|string} user
   * @param {Object|null} formFields
   * @param {Boolean} not_interested
   */
  setNotInterested(assignment, user, formFields, not_interested) {
    const assignmentId = this.shData.getModelId(assignment);
    const userId = this.shData.getModelId(user);

    const options = {
      jsonData: Object.assign({ not_interested }, not_interested ? formFields : null),
    };

    return this.get('api').post(`postings/${assignmentId}/users/${userId}/assignmentUserMeta`, options).then((response) => {
      return this._sideloadAssignmentUserMeta(assignmentId, response);
    }).catch((e) => {
      this.get('errorTracker').error('setNotInterested - failed to set not interested feedback ' + assignmentId, this, e);
      throw e;
    });
  },

  /**
   * @param {string} assignmentId
   * @param {Object} response
   * @return {DS.Model}
   * @private
   */
  _sideloadAssignmentUserMeta(assignmentId, response) {
    const store = this.get('store');

    store.pushPayload({
      data: {
        type: 'Assignment',
        id: assignmentId,
        relationships: {
          'assignment-user-meta': {
            data: {
              type: 'AssignmentUserMeta',
              id: response.data.id,
            },
          },
        },
      },
      included: [
        response.data,
      ],
    });

    return store.peekRecord('assignment', assignmentId);
  },

  /**
   * @param {DS.Model|string} assignment
   * @param {string} name
   * @param {string} email
   */
  refer(assignment, name, email) {
    const assignmentId = this.shData.getModelId(assignment);

    const options = {
      jsonData: {
        name,
        email,
      },
    };

    return this.get('api').post(`postings/${assignmentId}/refer`, options).then(() => { });
  },

  /**
   * Add followers for the given assignment id and update our assignment state.
   *
   * @param {String} assignmentId
   * @param {Array<String>} followerIds
   * @param {String[]=} opt_assignmentIncludes      Includes for the assignment load after the followers add.
   */
  addFollowers(assignmentId, followerIds, opt_assignmentIncludes = []) {
    let options = {
      jsonData: {
        'user-ids' : followerIds
      }
    };

    return new Promise((resolve, reject) => {
      this.get('api').post(`postings/${assignmentId}/addFollowers`, options).then((response) => {
        this.get('store').findRecord('assignment', assignmentId, {
          include: opt_assignmentIncludes.join(',')
        }).then(assignment => {
          let assignmentSummary = this.get('store').peekRecord('assignmentSummary', assignmentId);
          if (assignmentSummary) {
            assignmentSummary.set('watcherCount', assignment.get('followers.length'));
          }

          this._propagateFollowersToApplications(assignment, followerIds, true);

          resolve(true);
        }).catch((e) => {
          this.get('errorTracker').error('addFollowers - fail loading assignment ' + assignmentId, this, e);
          reject(e);
        });
      }).catch((e) => {
        this.get('errorTracker').error('addFollowers - fail adding followers ' + assignmentId, this, e);
        reject(e);
      });
    });
  },

  /**
   * Admin only call to approve a pending assignment.
   *
   * @param {Assignment} assignment
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  approveAssignment(assignment, opt_includes = []) {
    let includes = opt_includes.join(','),
      options = {};

    if (includes) {
      if (!options.params) {
        options.params = {};
      }

      options.params['includes'] = includes;
    }

    return new Promise((resolve, reject) => {
      this.get('api').post(`admin/postings/${assignment.get('id')}/approve`, options).then(response => {
        this.shData.pushResponseToStore(response);

        let updatedAssignment = this.get('store').peekRecord('assignment', response.data.id);
        resolve(updatedAssignment);
      }).catch(e => {
        this.get('errorTracker').error(`approveAssignment -- error approving assignment id ${assignment.get('id')}`, this, e);
        reject(e);
      });
    });
  },

  /**
   * Close the submissions for this assignment.
   *
   * @param {Assignment} assignment
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  closeAssignmentSubmissions(assignment, feedback, opt_includes = []) {
    const includes = opt_includes.join(',');
    const options = {
      params: {
        include: includes
      }
    };

    if (feedback) {
      options['jsonData'] = feedback;
    }

    return this.api.post(`postings/${assignment.id}/close`, options).then(response => {
      this.shData.pushResponseToStore(response);
      const updatedAssignment = this.store.peekRecord('assignment', response.data.id);

      return updatedAssignment;
    }).catch(e => {
      this.errorTracker.error('closeAssignmentSubmissions -- error closing assignment submissions', this, e);
      throw e;
    });
  },

  /**
   * Create a new assignment.
   *
   * @param {Object} createAssignmentData   @see AssignmentManager.validateAndGenerateAssignmentCreateData
   * * @param {Array=} opt_includes         Add your includes for the api.
   * @return {Ember.RSVP.Promise}
   */
  createAssignment(createAssignmentData, opt_includes = []) {

    if (createAssignmentData.deadline) {
      // Convert this deadline to UTC time from the local time on the client.
      createAssignmentData.deadline = convertToUTCDate(createAssignmentData.deadline, false);
    }

    const includes = opt_includes.join(',');
    const options = {
      jsonData : createAssignmentData,
      params: {
        include: includes
      }
    };

    return this.api.post(`postings`, options);
  },

  /**
   * Decline an assignment/posting by an admin only
   *
   * @param {Assignment} assignment
   * @param {String} opt_message
   * @param {String[]=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  declineAssignment(assignment, opt_message = '', opt_includes = []) {
    let options = {};

    if (opt_message) {
      options['jsonData'] = {
        message: opt_message
      };
    }

    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').post(`admin/postings/${assignment.get('id')}/decline`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let assignmentFromDB = this.get('store').peekRecord('assignment', response.data.id);
        resolve(assignmentFromDB);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error declining the posting. Please refresh and try again.',
          'declineAssignment -- error declining assignment id: ' + assignment.get('id'),
        );

        reject(e);
      });
    });
  },

  /**
   * Delete the assignment
   *
   * @param {Assignment} assignment
   * @return {Ember.RSVP.Promise}
   */
  deleteAssignment(assignment) {
    let options = {};

    return new Promise((resolve, reject) => {
      this.get('api').delete(`postings/${assignment.get('id')}`, options).then(response => {
        resolve(true);
      }).catch(e => {
        this.get('errorTracker').error('deleteAssignment -- error deleting assignment', this, e);
        reject(e);
      });
    });
  },

  /**
   * Invite a freelancer to an assignment
   *
   * @param {User[]}        usersToInvite
   * @param {Assignment}    assignment
   * @param {String=}       opt_initialMessage
   * @param {Boolean=}      opt_isHandleException     False if you don't want to auto handle the exception.
   * @return {Ember.RSVP.Promise}
   */
  inviteToAssignment(usersToInvite, assignment, opt_initialMessage, opt_isHandleException = true) {
    let options = {
      jsonData: {
        'user_ids' : usersToInvite.map(user => user.get('id'))
      }
    };

    if (opt_initialMessage) {
      options.jsonData['message'] = opt_initialMessage;
    }

    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').post(`postings/${assignment.get('id')}/invite`, options).then(() => {
        resolve(true);
      }).catch((e) => {
        if (opt_isHandleException) {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error inviting the user(s) to the posting. Please refresh and try again.',
            'inviteToAssignment -- error inviting user ids to assignment.' + usersToInvite.map(user => user.get('id')),
          );
        }

        reject(e);
      });
    });
  },

  /**
   * Load an assignment with the given ID.
   *
   * @param {String} assignmentId
   * @param {Array=} opt_includes     Add your includes for the api.
   * @return {Promise<Assignment>}
   */
  loadAssignment(assignmentId, opt_includes = []) {
    const includes = opt_includes.join(',');

    const options = {
      params: {
        include: includes
      },
    };

    return this.get('api').request(`postings/${assignmentId}`, options).then(response => {
      this.shData.pushResponseToStore(response);
      const assignment = this.get('store').peekRecord('assignment', response.data.id);
      return assignment;
    }).catch(e => {
      this.get('errorTracker').error(`loadAssignmentForId - ${assignmentId} - ${JSON.stringify(e)}`, this, e);
      throw e;
    });
  },

  /**
   * load assignments by a number of filters and sorts
   *
   * @param {String=} opt_status      of type ASSIGNMENT.STATUS
   * @param {Number=} opt_page        The current page number we'd like to fetch
   * @param {Number=} opt_pageCount   The count of how many items we want per page.
   * @param {String=} opt_cacheKey    supply a cacheKey to track the API call state.
   * @param {Array=} opt_includes     Add your includes for the api.
   * @param {Array=} opt_preserveRelationships  Optional relationships that would not be overridden for any existing assignments
   *                                            in the store.
   * @param {Object=} opt_filters     Additional filters to add.
   * @return {Ember.RSVP.Promise}
   */
  loadAssignmentsDefault(
  opt_status,
  opt_page,
  opt_pageCount,
  opt_cacheKey,
  opt_includes = [],
  opt_preserveRelationships = [],
  opt_filters = {}) {
    let options = {
      params: {
        format: 'default',
        // sort: sort,
        // secondary_sort: secondarySort,
        filter: Object.assign({}, opt_filters),
        page: opt_page || 1,
        count: opt_pageCount || 20
      }
    };

    let includes = opt_includes.join(',');
    if (includes) {
      options.params['include'] = includes;
    }

    if (opt_cacheKey) {
      options.params.cache_key = opt_cacheKey;
    }

    if (opt_status) {
      options.params.filter['status'] = opt_status;
    }

    return new Promise((resolve, reject) => {
      this.get('api').request('postings', options).then((response) => {
        this.shData.pushResponseToStore(response, opt_preserveRelationships);
        resolve(this.shData.getModelArrayFromResponse(response, 'assignment'));
      }).catch(e => {
        this.get('errorTracker').error('loadAssignmentsDefault - ' + e.toString(), this);
        reject(e);
      });
    });
  },

  /**
   * Load the assignment dashboard list object from the server and parse in all the information
   * into our internal stores.
   *
   * @param {String} status               of type ASSIGNMENT.STATUS
   * @param {String} sort                 of type POSTINGS_SORT_API_ENUMS.*
   * @param {String} networkFilter        of type POSTINGS_NETWORK_FILTER_API_ENUMS.*
   * @param {Number|null} companyTeamFilter of type CompanyTeam.id
   * @param {String} secondarySort        of type ICON_SORTS.*
   * @param {Number} page                 The current page number we'd like to fetch
   * @param {Number} count                The count of how many items we want per page.
   * @param {String} cacheKey             The cache key to ensure our results stay in sync b/t pages.
   * @param {Array=} optIncludes          Add your includes for the api.
   */
  loadAssignmentDashboardListRequest(status, sort, networkFilter, companyTeamFilter, secondarySort, page, count, cacheKey, optIncludes = []) {
    let options = {
      params: {
        format: 'dashboard',
        sort: sort,
        secondary_sort: secondarySort,
        filter: {},
        page: page,
        cache_key: cacheKey,
        count: count
      }
    };

    const includes = optIncludes.join(',');
    if (includes) {
      options.params['include'] = includes;
    }

    if (networkFilter) {
      options.params.filter['network'] = networkFilter;
    }
    if (companyTeamFilter) {
      options.params.filter['company_team'] = companyTeamFilter;
    }
    if (status) {
      options.params.filter['status'] = status;
    }

    return new Promise((resolve, reject) => {
      this.get('api').request('postings', options).then((response) => {
        this._parseDashboardList(response, resolve, reject);
      }).catch(e => this.get('errorTracker').error('loadAssignmentDashboardListRequest - ' + e.toString(), this));
    });
  },

  loadAssignmentInvites(assignment, filters, includes) {
    const options = {
      params: {},
    };

    if (includes) {
      options.params['include'] = includes;
    }

    if (filters) {
      options.params['filter'] = filters;
    }

    return this.api.request(`postings/${assignment.id}/invites`, options);
  },

  /**
   * Load the assignments for a given user.
   *
   * @param {User} user
   * @return {Ember.RSVP.Promise}   Will resolve with list of assignments.
   */
  loadPostingsForUser(user) {
    let params = {
      filter: {
        status: 'live',
        company: user.get('company.id')
      }
    };

    return this.get('store').query('assignment', params);
  },

  /**
   * Remove followers for the given assignment id and update our assignment state.
   *
   * @param {String} assignmentId
   * @param {Array<String>} followerIds
   */
  removeFollowers(assignmentId, followerIds) {
    let options = {
      params: {},
      jsonData: {
        'user-ids' : followerIds
      }
    };

    return new Promise((resolve, reject) => {
      this.get('api').post(`postings/${assignmentId}/removeFollowers`, options).then((response) => {
        this.get('store').findRecord('assignment', assignmentId, {
          include: 'followers'
        }).then(assignment => {
          let assignmentSummary = this.get('store').peekRecord('assignmentSummary', assignmentId);
          if (assignmentSummary) {
            assignmentSummary.set('watcherCount', assignment.get('followers.length'));
          }

          this._propagateFollowersToApplications(assignment, followerIds, false);

          resolve(true);
        }).catch((e) => {
          this.get('errorTracker').error('removeFollowers - could not load assignment' + assignmentId, this);
          reject(e);
        });
      }).catch((e) => {
        this.get('errorTracker').error('removeFollowers - could not removeFollowers' + assignmentId, this);
        reject(e);
      });
    });
  },

  /**
   * Given the search text make a database call to find assignment summaries for this value.
   *
   * @param {String} searchTextValue
   * @param {Array=} optIncludes     Add your includes for the api.
   */
  searchDashboardList(searchTextValue, optIncludes = []) {
    let options = {
      params: {
        format: 'dashboard',
        search: searchTextValue
      }
    };

    const includes = optIncludes.join(',');
    if (includes) {
      options.params['include'] = includes;
    }

    return new Promise((resolve, reject) => {
      this.get('api').request('postings', options).then((response) => {
        this._parseDashboardList(response, resolve, reject);
      }).catch(e => {
        this.get('errorTracker').error('searchDashboardList - search fail', this);
        reject(e);
      });
    });
  },

  /**
   * Set select status of assignment to value of selectStatus
   *
   * @param {Boolean} selectStatus
   */
  setSelectStatus(assignmentId, selectStatus) {
    const options = {
      params: {
        selectStatus
      }
    }

    return this.api.post(`postings/${assignmentId}/setSelect`, options);
  },

  /**
   * Add or remove an assignment's association to a team
   *
   * @param {Number|null} selectStatus
   */
  setCompanyTeam(assignmentId, companyTeamId) {
    const options = {
      jsonData: {
        companyTeamId,
      }
    }

    return this.api.put(`postings/${assignmentId}/setTeam`, options);
  },

  /**
   * Make a call to test for how many matches we'd have for a given assignment.
   *
   * @param {Object} params      Of type ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.*
   * @param {String=} opt_cacheKey    String for the cache_key to pass down to the API.
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  testAssignmentMatches(params, opt_cacheKey = '', opt_includes = []) {
    let options = {
      jsonData : params,
      params : {}
    };

    let includes = '';
    if (opt_includes) {
      includes = opt_includes.join(',');
    }

    if (includes) {
      options.params['include'] = includes;
    }

    if (opt_cacheKey) {
      options.params['cache_key'] = opt_cacheKey;
    }

    return new Promise((resolve, reject) => {
      this.get('api').post(
        `postings/test-blast`,
        options
      ).then(response => {
        this.shData.pushResponseToStore(response);
        let blastTest = this.get('store').peekRecord('assignmentBlastTest', response.data.id);
        resolve(blastTest);
      }).catch(e => {
        this.get('errorTracker').error('testAssignmentMatches -- error testing matches', this, e);
        reject(e);
      });
    });
  },

  trackUserView(assignmentId, userId) {
    return this.api.post(`postings/${assignmentId}/users/${userId}/trackView`, {});
  },

  /**
   * Update an existing assignment
   *
   * @param {Assignment} assignment
   * @param {Object} updateAssignmentData   @see AssignmentManager.validateAndGenerateAssignmentCreateData
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  updateAssignment(assignment, updateAssignmentData, opt_includes = []) {
    let options = {
      jsonData : updateAssignmentData,
      params : {}
    };

    let includes = '';
    if (opt_includes) {
      includes = opt_includes.join(',');
    }

    if (includes) {
      options.params['include'] = includes;
    }

    if (updateAssignmentData.deadline) {
      // Convert this deadline to UTC time from the local time on the client.
      updateAssignmentData.deadline = convertToUTCDate(updateAssignmentData.deadline, false);
    }

    return new Promise((resolve, reject) => {
      this.get('api').put(
        `postings/${assignment.get('id')}`,
        options).then(response => {
        this.shData.pushResponseToStore(response);

        let updatedAssignment = this.get('store').peekRecord('assignment', response.data.id);
        resolve(updatedAssignment);
      }).catch(e => {
        this.get('errorTracker').error('updateAssignment -- error updating assignment', this, e);
        reject(e);
      });
    });
  },

  /**
   * Parse the response into the store and appropriate objects.
   *
   * @param {Object} response   The json object
   * @param {Function} resolve  callback function for Promise.  Will be resolved with the DashboardList
   * @param {Function} reject   callback function for Promise
   * @private
   */
  _parseDashboardList(response, resolve, reject) {
    if (!response.data) {
      this.get('errorTracker').error('_parseDashboardList no data - ', this);
      reject(false);
    }

    const store = this.store;
    const assignmentDashboardList = store.createRecord('assignmentDashboardList');

    this.shData.pushResponseToStore(response);

    assignmentDashboardList.set('assignmentSummaries', this.shData.getModelArrayFromResponse(response, 'assignmentSummary'));

    //add meta info
    let meta = response.meta;
    if (meta) {
      assignmentDashboardList.set('liveCount', meta.live_count);
      assignmentDashboardList.set('draftCount', meta.draft_count);
      assignmentDashboardList.set('closedCount', meta.closed_count);
      assignmentDashboardList.set('inReviewCount', meta.in_review_count);
      assignmentDashboardList.set('liveIsRead', meta.live_is_read);
      assignmentDashboardList.set('draftIsRead', meta.draft_is_read);
      assignmentDashboardList.set('closedIsRead', meta.closed_is_read);
      assignmentDashboardList.set('inReviewIsRead', meta.in_review_is_read);
    } else {
      this.get('errorTracker').error('_parseDashboardList no meta - ', this);
    }

    //add pagination
    let paginationMeta = meta.pagination,
      pagination = store.createRecord('pagination');
    if (paginationMeta) {
      pagination.set('total', paginationMeta.total);
      pagination.set('count', paginationMeta.count);
      pagination.set('perPage', paginationMeta.per_page);
      pagination.set('currentPage', paginationMeta.current_page);
      pagination.set('totalPages', paginationMeta.total_pages);
    } else {
      this.get('errorTracker').error('_parseDashboardList no pagination - ', this);
    }

    assignmentDashboardList.set('pagination', pagination);
    resolve(assignmentDashboardList);
  },

  /**
   * Propagate all followers to the child applications
   *
   * @param {Assignment} assignment
   * @param {String[]} followerIds    Assumes these users have been added to the store already.
   * @param {Boolean} isAdding        True will add the followers, false will remove
   * @private
   */
  _propagateFollowersToApplications(assignment, followerIds, isAdding) {
    let allApplications = this.get('store').peekAll('application');
    if (!allApplications.get('length')) {
      return;
    }

    let usersToAdd = A();
    followerIds.forEach(followerId => {
      usersToAdd.push(this.get('store').peekRecord('user', followerId));
    });

    allApplications.forEach(application => {
      if (application.get('assignment.id') === assignment.get('id')) {
        usersToAdd.forEach(follower => {
          if (isAdding) {
            application.get('followers').addObject(follower);
          } else {
            application.get('followers').removeObject(follower);
          }

        });
      }
    });
  }
});
